import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';

import styles from './styles.module.css';
import ChevronIcon from '@/assets/icons/chevron.svg?react';
import {
  getDateCustomFormatOut,
  MONTH_YEAR_LONG,
  SHORT_YEAR_FORMAT_DATE,
  US_SHORT_DATE
} from '@/solaris-lens-common-lib/src/utils/dates';

export interface DateRangeValue {
  start: string;
  end: string;
}

interface DateRangeInputProps {
  startLabel?: string;
  endLabel?: string;
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  id?: string;
  customClass?: string;
}

interface RangePreset {
  id: string;
  label: string;
  getRange: () => DateRangeValue;
}

const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const CALENDAR_CELLS = 42;

const toIso = (date: Dayjs): string => date.format(SHORT_YEAR_FORMAT_DATE);

const PRESETS: RangePreset[] = [
  {
    id: 'today',
    label: 'Today',
    getRange: () => ({ start: toIso(dayjs()), end: toIso(dayjs()) })
  },
  {
    id: 'yesterday',
    label: 'Yesterday',
    getRange: () => {
      const day = dayjs().subtract(1, 'day');
      return { start: toIso(day), end: toIso(day) };
    }
  },
  {
    id: 'thisWeek',
    label: 'This week',
    getRange: () => ({
      start: toIso(dayjs().startOf('week')),
      end: toIso(dayjs())
    })
  },
  {
    id: 'lastWeek',
    label: 'Last week',
    getRange: () => {
      const start = dayjs().subtract(1, 'week').startOf('week');
      return { start: toIso(start), end: toIso(start.endOf('week')) };
    }
  },
  {
    id: 'lastMonth',
    label: 'Last month',
    getRange: () => {
      const start = dayjs().subtract(1, 'month').startOf('month');
      return { start: toIso(start), end: toIso(start.endOf('month')) };
    }
  },
  {
    id: 'last30',
    label: 'Last 30 days',
    getRange: () => ({
      start: toIso(dayjs().subtract(29, 'day')),
      end: toIso(dayjs())
    })
  },
  {
    id: 'last60',
    label: 'Last 60 days',
    getRange: () => ({
      start: toIso(dayjs().subtract(59, 'day')),
      end: toIso(dayjs())
    })
  }
];

const formatDisplay = (value: string): string =>
  value ? getDateCustomFormatOut(value, US_SHORT_DATE) : '';

const buildMonthCells = (month: Dayjs): Dayjs[] => {
  const gridStart = month.startOf('month').startOf('week');
  return Array.from({ length: CALENDAR_CELLS }, (_, index) =>
    gridStart.add(index, 'day')
  );
};

interface MonthCalendarProps {
  month: Dayjs;
  range: DateRangeValue;
  onSelectDay: (isoDate: string) => void;
  showPrev: boolean;
  showNext: boolean;
  onPrev: () => void;
  onNext: () => void;
}

function MonthCalendar({
  month,
  range,
  onSelectDay,
  showPrev,
  showNext,
  onPrev,
  onNext
}: MonthCalendarProps) {
  const cells = useMemo(() => buildMonthCells(month), [month]);
  const start = range.start ? dayjs(range.start).startOf('day') : null;
  const end = range.end ? dayjs(range.end).startOf('day') : null;

  return (
    <div className={styles.calendarView}>
      <div className={styles.monthSelector}>
        <button
          type="button"
          className={`${styles.navButton} ${showPrev ? '' : styles.navHidden}`}
          onClick={onPrev}
          aria-label="Previous month"
          tabIndex={showPrev ? 0 : -1}
        >
          <ChevronIcon className={styles.navChevronLeft} aria-hidden="true" />
        </button>
        <span className={styles.monthLabel}>
          {month.format(MONTH_YEAR_LONG)}
        </span>
        <button
          type="button"
          className={`${styles.navButton} ${showNext ? '' : styles.navHidden}`}
          onClick={onNext}
          aria-label="Next month"
          tabIndex={showNext ? 0 : -1}
        >
          <ChevronIcon className={styles.navChevronRight} aria-hidden="true" />
        </button>
      </div>

      <div className={styles.weekdaysRow}>
        {WEEKDAYS.map(weekday => (
          <span key={weekday} className={styles.weekdayLabel}>
            {weekday}
          </span>
        ))}
      </div>

      <div className={styles.daysGrid}>
        {cells.map(cell => {
          const day = cell.startOf('day');
          const isOutside = !cell.isSame(month, 'month');
          const inRange =
            start && end && !day.isBefore(start) && !day.isAfter(end);
          const isStart = start && day.isSame(start, 'day');
          const isEnd = end && day.isSame(end, 'day');
          const isToday = day.isSame(dayjs(), 'day');

          const cellClass = [
            styles.dayCell,
            isOutside ? styles.dayOutside : '',
            inRange ? styles.dayInRange : '',
            isStart ? styles.dayRangeStart : '',
            isEnd ? styles.dayRangeEnd : '',
            isToday ? styles.dayToday : ''
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button
              key={cell.format(SHORT_YEAR_FORMAT_DATE)}
              type="button"
              id={`dateRangeDay-${cell.format(SHORT_YEAR_FORMAT_DATE)}`}
              className={cellClass}
              onClick={() => onSelectDay(cell.format(SHORT_YEAR_FORMAT_DATE))}
            >
              {cell.date()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function DateRangeInput({
  startLabel = 'Start',
  endLabel = 'End',
  value,
  onChange,
  id,
  customClass
}: DateRangeInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingStart, setPendingStart] = useState<string | null>(null);
  const [viewMonth, setViewMonth] = useState<Dayjs>(() =>
    (value.start ? dayjs(value.start) : dayjs()).startOf('month')
  );
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setPendingStart(null);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setPendingStart(null);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const openPopover = () => {
    setViewMonth((value.start ? dayjs(value.start) : dayjs()).startOf('month'));
    setPendingStart(null);
    setIsOpen(true);
  };

  const activePresetId = useMemo(() => {
    const match = PRESETS.find(preset => {
      const range = preset.getRange();
      return range.start === value.start && range.end === value.end;
    });
    return match?.id;
  }, [value.start, value.end]);

  const handleSelectDay = (isoDate: string) => {
    if (!pendingStart) {
      setPendingStart(isoDate);
      onChange({ start: isoDate, end: isoDate });
      return;
    }
    const isBeforeAnchor = dayjs(isoDate).isBefore(dayjs(pendingStart));
    const nextRange = isBeforeAnchor
      ? { start: isoDate, end: pendingStart }
      : { start: pendingStart, end: isoDate };
    onChange(nextRange);
    setPendingStart(null);
    setIsOpen(false);
  };

  const handlePreset = (preset: RangePreset) => {
    onChange(preset.getRange());
    setPendingStart(null);
    setIsOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className={`${styles.wrapper} ${customClass || ''}`}
    >
      <button
        type="button"
        id={id || 'dateRangeTrigger'}
        className={styles.trigger}
        onClick={() => (isOpen ? setIsOpen(false) : openPopover())}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <span className={styles.triggerField}>
          <span className={styles.triggerLabel}>{startLabel}</span>
          <span className={styles.triggerValue}>
            {formatDisplay(value.start)}
          </span>
        </span>
        <span className={styles.triggerField}>
          <span className={styles.triggerLabel}>{endLabel}</span>
          <span className={styles.triggerValue}>
            {formatDisplay(value.end)}
          </span>
        </span>
      </button>

      {isOpen && (
        <div className={styles.popover} role="dialog">
          <div className={styles.presets}>
            {PRESETS.map(preset => (
              <button
                key={preset.id}
                type="button"
                id={`dateRangePreset-${preset.id}`}
                className={`${styles.preset} ${activePresetId === preset.id ? styles.presetActive : ''}`}
                onClick={() => handlePreset(preset)}
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className={styles.calendars}>
            <MonthCalendar
              month={viewMonth}
              range={value}
              onSelectDay={handleSelectDay}
              showPrev
              showNext={false}
              onPrev={() => setViewMonth(prev => prev.subtract(1, 'month'))}
              onNext={() => setViewMonth(prev => prev.add(1, 'month'))}
            />
            <MonthCalendar
              month={viewMonth.add(1, 'month')}
              range={value}
              onSelectDay={handleSelectDay}
              showPrev={false}
              showNext
              onPrev={() => setViewMonth(prev => prev.subtract(1, 'month'))}
              onNext={() => setViewMonth(prev => prev.add(1, 'month'))}
            />
          </div>
        </div>
      )}
    </div>
  );
}
